"use client";

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const TASKS_INIT = [
    { id: 1, labelKey: "Cw_day_t1", min: 30 },
    { id: 2, labelKey: "Cw_day_t2", min: 45 },
    { id: 3, labelKey: "Cw_day_t3", min: 20 },
    { id: 4, labelKey: "Cw_day_t4", min: 25 },
    { id: 5, labelKey: "Cw_day_t5", min: 15 },
];

function fmt(m) {
    if (m <= 0) return "0 min";
    const h = Math.floor(m / 60);
    const r = m % 60;
    if (h && r) return `${h} h ${r} min`;
    if (h) return `${h} h`;
    return `${m} min`;
}

export default function CwDay() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [tasks, setTasks] = useState(() =>
        TASKS_INIT.map((x) => ({ ...x, status: "todo" })),
    );
    const timersRef = useRef([]);

    useEffect(() => {
        const timers = timersRef.current;
        return () => timers.forEach((id) => clearTimeout(id));
    }, []);

    function setStatus(id, status) {
        setTasks((ts) => ts.map((x) => (x.id === id ? { ...x, status } : x)));
    }

    function delegate(id) {
        const tk = tasks.find((x) => x.id === id);
        if (!tk || tk.status !== "todo") return;
        if (reduce) {
            setStatus(id, "done");
            return;
        }
        setStatus(id, "working");
        const tm = setTimeout(() => setStatus(id, "done"), 850);
        timersRef.current.push(tm);
    }

    function delegateAll() {
        if (reduce) {
            setTasks((ts) => ts.map((x) => ({ ...x, status: "done" })));
            return;
        }
        const todo = tasks.filter((x) => x.status === "todo");
        todo.forEach((x, i) => {
            const tm1 = setTimeout(() => setStatus(x.id, "working"), i * 240);
            const tm2 = setTimeout(() => setStatus(x.id, "done"), i * 240 + 850);
            timersRef.current.push(tm1, tm2);
        });
    }

    function reset() {
        setTasks(TASKS_INIT.map((x) => ({ ...x, status: "todo" })));
    }

    const timeSaved = tasks
        .filter((x) => x.status === "done")
        .reduce((a, b) => a + b.min, 0);
    const allDone = tasks.every((x) => x.status === "done");
    const remaining = tasks.filter((x) => x.status === "todo").length;

    return (
        <section id="cw-day" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Cw_day_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Cw_day_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows (corail Claude) */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-orange-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:claude"
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                        />
                        {t("Cw_day_file")}
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-orange-300">
                            {t("Cw_day_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-7">
                    {/* Bandeau temps gagné */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl bg-gradient-to-r from-orange-500/15 to-rose-500/10 ring-1 ring-orange-400/25 px-5 py-4 mb-5">
                        <div>
                            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-orange-300 mb-0.5">
                                {t("Cw_day_saved_label")}
                            </div>
                            <motion.div
                                key={timeSaved}
                                initial={reduce ? false : { scale: 0.8, opacity: 0.4 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 380, damping: 18 }}
                                className="text-3xl font-bold text-white tabular-nums"
                            >
                                {fmt(timeSaved)}
                            </motion.div>
                        </div>
                        <button
                            type="button"
                            onClick={allDone ? reset : delegateAll}
                            className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 hover:bg-orange-400 px-5 py-2.5 text-[14px] font-semibold text-white transition active:scale-95"
                        >
                            <SolarIcon
                                name={allDone ? "restart-bold-duotone" : "magic-stick-3-bold"}
                                className="w-4 h-4"
                            />
                            {allDone ? t("Cw_day_reset") : t("Cw_day_all")}
                        </button>
                    </div>

                    {/* Liste des tâches */}
                    <ul className="flex flex-col gap-2.5">
                        {tasks.map((task) => (
                            <li
                                key={task.id}
                                className={`flex items-center gap-3 rounded-xl ring-1 px-4 py-3 transition-colors duration-300 ${
                                    task.status === "done"
                                        ? "bg-emerald-500/[0.06] ring-emerald-400/25"
                                        : "bg-white/[0.04] ring-white/10"
                                }`}
                            >
                                <span className="shrink-0">
                                    {task.status === "done" ? (
                                        <SolarIcon
                                            name="check-circle-bold"
                                            className="w-5 h-5 text-emerald-400"
                                        />
                                    ) : task.status === "working" ? (
                                        <SolarIcon
                                            name="refresh-bold-duotone"
                                            className="w-5 h-5 text-orange-300 animate-spin"
                                        />
                                    ) : (
                                        <span className="block w-5 h-5 rounded-full ring-2 ring-white/20" />
                                    )}
                                </span>

                                <span className="flex-1 min-w-0">
                                    <span
                                        className={`block text-[13.5px] leading-tight ${
                                            task.status === "done"
                                                ? "text-gray-500 line-through"
                                                : "text-gray-100"
                                        }`}
                                    >
                                        {t(task.labelKey)}
                                    </span>
                                    <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 mt-0.5">
                                        <SolarIcon
                                            name="clock-circle-bold-duotone"
                                            className="w-3 h-3"
                                        />
                                        {t("Cw_day_about")} {task.min} min
                                    </span>
                                </span>

                                <span className="shrink-0">
                                    {task.status === "todo" && (
                                        <button
                                            type="button"
                                            onClick={() => delegate(task.id)}
                                            className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] hover:bg-orange-500/20 ring-1 ring-white/15 hover:ring-orange-400/40 px-3 py-1.5 text-[12px] font-semibold text-gray-100 transition active:scale-95"
                                        >
                                            <Icon
                                                icon="logos:claude"
                                                className="w-3.5 h-3.5"
                                                aria-hidden="true"
                                            />
                                            {t("Cw_day_delegate")}
                                        </button>
                                    )}
                                    {task.status === "working" && (
                                        <span className="text-[11.5px] text-orange-300 font-medium">
                                            {t("Cw_day_working")}
                                        </span>
                                    )}
                                    {task.status === "done" && (
                                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-300">
                                            <Icon
                                                icon="logos:claude"
                                                className="w-3 h-3"
                                                aria-hidden="true"
                                            />
                                            {t("Cw_day_byclaude")}
                                        </span>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>

                    {/* Bannière fin */}
                    <AnimatePresence>
                        {allDone && (
                            <motion.div
                                initial={reduce ? false : { opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className="mt-4 flex items-start gap-3 rounded-2xl bg-emerald-500/10 ring-1 ring-emerald-400/30 px-5 py-4"
                            >
                                <SolarIcon
                                    name="cup-star-bold-duotone"
                                    className="w-6 h-6 text-emerald-300 shrink-0"
                                />
                                <p className="text-[14px] text-emerald-100 leading-snug mb-0">
                                    {t.rich("Cw_day_done_banner", {
                                        b: (chunks) => (
                                            <strong className="text-white font-semibold">
                                                {chunks}
                                            </strong>
                                        ),
                                        time: fmt(timeSaved),
                                    })}
                                </p>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {!allDone && remaining < tasks.length && (
                        <p className="mt-3 text-[12px] text-gray-500">
                            {t("Cw_day_remaining", { count: remaining })}
                        </p>
                    )}
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Cw_day_caption")}
            </p>
        </section>
    );
}
